import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Snackbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";

/* ============================================================================
 * In-app branch switcher
 * ----------------------------------------------------------------------------
 * The sibling of StoreSwitcher, and deliberately a separate control. A store is
 * a company; a branch is one of the places that company trades from. Someone who
 * owns two stores with three branches each needs to say which of the two things
 * they are moving, and one combined list of "everywhere you could be" would make
 * that ambiguous exactly when it matters.
 *
 * WHO SEES IT
 * Only a Store Head or Service Head, and only where the store has more than one
 * branch. Everyone else belongs to one branch and cannot leave it — see
 * utils/roles.js BRANCH_PINNED_ROLES. The server enforces the same rule in
 * /api/auth/switch-branch; this only decides whether to draw the control, and a
 * hidden button is not a permission.
 *
 * WHY IT DOES NOT RELOAD THE PAGE
 * StoreSwitcher reloads because settings, financial year, tasks, templates and
 * every cached row belong to the store being left. A branch move keeps all of
 * that — only the branch changes — so a reload would be a second of white screen
 * for nothing. That stops being true once module data is branch-scoped: at that
 * point screens holding fetched rows will have to refetch on a branch change.
 * See the note on switchBranch in AutoTaskContext.
 * ========================================================================== */

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

const BLUE = "#2563EB";
const GREEN = "#059669";
const INK = "#0F172A";
const INK_MUTED = "#64748B";
const INK_FAINT = "#94A3B8";
const BORDER = "#E2E8F0";

// Storage is the fallback for the same reason StoreSwitcher needs one: the pawn
// routes render without waiting for this context to hydrate, so a control in
// that rail can be clicked while currentStoreInfo is still null.
const readStored = (key) => {
  try {
    return JSON.parse(localStorage.getItem(`CapacitorStorage.${key}`)) || {};
  } catch {
    return {};
  }
};

/**
 * The branches of the store this session is in, plus whether this person may
 * move between them.
 *
 * Fetched rather than read off the cached store row: `store.branches` is a
 * snapshot taken at login and a branch added since would be missing from it,
 * which is the one moment the switcher matters most.
 *
 *   const { branches, loading, canSwitch } = useSwitchableBranches();
 *   canSwitch && <button …/>
 *
 * `canSwitch` already folds in the "more than one branch" test, so a host can
 * gate its control on that single value.
 *
 * `enabled: false` holds the fetch back. For a host that only needs the list
 * once something has happened — the create guard, which wants it only if
 * somebody tries to save while viewing every branch — so that a dialog which
 * usually never opens does not cost a request on every screen it sits on.
 */
export const useSwitchableBranches = ({ enabled = true } = {}) => {
  const {
    currentStoreInfo,
    currentBranch,
    canSwitchBranch,
    canViewAllBranches,
    viewingAllBranches,
  } = useContext(AutoTaskAuthContext) || {};

  const store = currentStoreInfo || readStored("current_store_info");
  const storeId = store?.id;

  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (storeId === undefined || storeId === null) return;

    let cancelled = false;
    setLoading(true);

    axios
      .get(`${API_BASE_URL}/api/auth/stores/${storeId}/branches`)
      .then((res) => {
        if (!cancelled) setBranches(res?.data?.data || []);
      })
      .catch(() => {
        if (!cancelled) setBranches([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [storeId, enabled]);

  const canSwitch = Boolean(canSwitchBranch) && branches.length > 1;

  return {
    branches,
    loading,
    currentBranch: currentBranch || null,
    // A store with one branch has nowhere to go, so the control is hidden even
    // for the roles allowed to use it.
    canSwitch,
    // Whether the dialog should offer "All branches" as well.
    //
    // Deliberately ANDed with canSwitch rather than taken from the session
    // field alone. The server also marks a Service HOD and Service Staff as
    // able to view every branch — which is true, they are not in any branch —
    // but they cannot switch, so they never open this dialog, and their reports
    // already default to every branch because they have no branch of their own.
    // Offering them a toggle between "all" and "all" would be a control that
    // appears to do nothing.
    canViewAll: canSwitch && Boolean(canViewAllBranches),
    viewingAll: Boolean(viewingAllBranches),
  };
};

/**
 * The picker. `branches` / `loading` come from useSwitchableBranches so the host
 * can gate its own control on the same data without a second fetch.
 */
export const BranchSwitcherDialog = ({
  open,
  onClose,
  branches = [],
  loading,
  // Both come from useSwitchableBranches, so the host that already called it to
  // decide whether to draw its control does not pay for a second fetch.
  canViewAll = false,
  viewingAll = false,
  // Why the dialog opened, when it opened by itself. Set by the create guard,
  // which turns this same picker into the "which branch is this for?" prompt
  // rather than growing a second dialog that lists the same branches.
  reason = null,
  // What the title says. "Switch branch" is wrong when the dialog is asking
  // which branch something belongs to.
  title = "Switch branch",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentStoreInfo, currentBranch, switchBranch, setViewingAllBranches } =
    useContext(AutoTaskAuthContext) || {};

  const store = currentStoreInfo || readStored("current_store_info");
  const [switchingTo, setSwitchingTo] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);

  const currentId = currentBranch?.id ?? null;

  const handleSwitch = async (branch) => {
    // Already here AND already looking at just here: nothing to do. The second
    // half matters — somebody in the all-branches view picking their own branch
    // is asking to narrow back down to it, not asking for nothing.
    if (branch.id === currentId && !viewingAll) {
      onClose?.();
      return;
    }

    setSwitchingTo(branch);
    try {
      // Leaving the all-branches view is what picking a branch MEANS, and it
      // has to happen even when no switch call is needed.
      setViewingAllBranches?.(false);
      if (branch.id !== currentId) await switchBranch(branch.id);
      // No reload to hide behind, so the dialog closes itself and says so.
      onClose?.();
      setDone(`Now working in ${branch.name}`);
    } catch (err) {
      setError(err?.message || "Failed to switch branch");
    } finally {
      setSwitchingTo(null);
    }
  };

  // "All branches" is a VIEW, not a place — there is no switch-branch call
  // behind it. The session stays in the branch it was in (which is why the row
  // still names it below); only what reports aggregate widens, and creating
  // anything is blocked while it is on, because a new row has to record one
  // branch and there is no honest way to pick one from here.
  const handleViewAll = () => {
    setViewingAllBranches?.(true);
    onClose?.();
    setDone("Now viewing every branch — you cannot add anything until you pick one");
  };

  return (
    <>
      {/* zIndex mirrors StoreSwitcher's, and for the same reason: the Daily Tick
          header is 2000 and the mobile drawer paper 5500, against a default
          Dialog of 1300. Left at the default, the full-screen mobile dialog
          opens underneath the header with its title and close button hidden. */}
      <Dialog
        open={!!open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
        sx={{ zIndex: 6000 }}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : "14px",
            ...(isMobile && { pt: "var(--safe-area-top, 0px)" }),
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, pr: 6 }}>
          <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: INK }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: INK_MUTED }}>
            {store?.name || "This store"}
            {currentBranch ? ` · currently in ${currentBranch.name}` : ""}
          </Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              position: "absolute",
              top: isMobile ? "calc(var(--safe-area-top, 0px) + 8px)" : 8,
              right: 8,
              color: INK_MUTED,
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 22 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 1.5, pb: 2 }}>
          {reason && (
            <Alert
              severity="info"
              sx={{ mb: 1.5, fontSize: "0.8rem", alignItems: "center" }}
            >
              {reason}
            </Alert>
          )}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={22} sx={{ color: BLUE }} />
            </Box>
          ) : branches.length === 0 ? (
            <Typography
              sx={{ fontSize: "0.85rem", color: INK_MUTED, px: 1, py: 2 }}
            >
              This store has no other branch to switch to.
            </Typography>
          ) : (
            <List disablePadding>
              {/* Above the branches, not among them: it is a different kind of
                  thing. Every row below is a place you can stand in; this one
                  is a way of looking at all of them at once. */}
              {canViewAll && (
                <ListItemButton
                  onClick={handleViewAll}
                  disabled={!!switchingTo}
                  sx={{
                    borderRadius: "10px",
                    border: `1px solid ${viewingAll ? GREEN : BORDER}`,
                    bgcolor: viewingAll ? "#F0FDF4" : "transparent",
                    mb: 1,
                    gap: 1.5,
                  }}
                >
                  {viewingAll ? (
                    <CheckCircleRoundedIcon
                      sx={{ fontSize: 20, color: GREEN }}
                    />
                  ) : (
                    <PublicRoundedIcon sx={{ fontSize: 20, color: BLUE }} />
                  )}
                  <ListItemText
                    disableTypography
                    primary={
                      <Typography
                        sx={{ fontSize: "0.9rem", fontWeight: 600, color: INK }}
                      >
                        All branches
                      </Typography>
                    }
                    secondary={
                      <Typography
                        sx={{ fontSize: "0.72rem", color: INK_FAINT }}
                      >
                        {viewingAll
                          ? "You are viewing every branch — nothing can be added"
                          : `Reports across all ${branches.length} branches`}
                      </Typography>
                    }
                  />
                  {!viewingAll && (
                    <ArrowForwardRoundedIcon
                      sx={{ fontSize: 18, color: INK_FAINT }}
                    />
                  )}
                </ListItemButton>
              )}

              {branches.map((branch) => {
                // The branch the SESSION is in, which is not the same question
                // as what is being looked at: in the all-branches view the
                // session is still in one branch, and that row must stop
                // claiming to be the current view or two rows read as selected.
                const isSession = branch.id === currentId;
                const isCurrent = isSession && !viewingAll;
                const busy = switchingTo?.id === branch.id;

                return (
                  <ListItemButton
                    key={branch.id}
                    onClick={() => handleSwitch(branch)}
                    disabled={!!switchingTo}
                    sx={{
                      borderRadius: "10px",
                      border: `1px solid ${isCurrent ? GREEN : BORDER}`,
                      bgcolor: isCurrent ? "#F0FDF4" : "transparent",
                      mb: 1,
                      gap: 1.5,
                    }}
                  >
                    {isCurrent ? (
                      <CheckCircleRoundedIcon
                        sx={{ fontSize: 20, color: GREEN }}
                      />
                    ) : (
                      <PlaceRoundedIcon sx={{ fontSize: 20, color: BLUE }} />
                    )}
                    <ListItemText
                      disableTypography
                      primary={
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            minWidth: 0,
                          }}
                        >
                          <Typography
                            noWrap
                            sx={{
                              fontSize: "0.9rem",
                              fontWeight: 600,
                              color: INK,
                              minWidth: 0,
                            }}
                          >
                            {branch.name}
                          </Typography>
                          {/* The code is shown because it is what people say out
                              loud and type into other systems — and because two
                              branches can share a name far more easily than a
                              code, which is unique within the store. */}
                          <Chip
                            label={branch.code}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: "0.65rem",
                              fontWeight: 700,
                              bgcolor: "#EFF6FF",
                              color: "#1E40AF",
                              border: "1px solid #BFDBFE",
                            }}
                          />
                          {branch.is_main && (
                            <Chip
                              label="Main"
                              size="small"
                              sx={{
                                height: 18,
                                fontSize: "0.65rem",
                                fontWeight: 700,
                                bgcolor: "#F1F5F9",
                                color: "#334155",
                                border: `1px solid ${BORDER}`,
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography
                          noWrap
                          sx={{ fontSize: "0.72rem", color: INK_FAINT }}
                        >
                          {isCurrent
                            ? "You are here"
                            : isSession
                              ? "Your branch — pick it to narrow back down"
                              : branch.address || "No address on file"}
                        </Typography>
                      }
                    />
                    {busy ? (
                      <CircularProgress size={16} sx={{ color: BLUE }} />
                    ) : (
                      !isCurrent && (
                        <ArrowForwardRoundedIcon
                          sx={{ fontSize: 18, color: INK_FAINT }}
                        />
                      )
                    )}
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={!!done}
        autoHideDuration={3000}
        onClose={() => setDone(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" variant="filled">
          {done}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={4000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BranchSwitcherDialog;
